import './CreateUserApplicationRole.scss';

import { ApplicationSelect, SelectedApp } from '~/components/ApplicationSelect/ApplicationSelect';
import { Button, Card, Col, Form, FormCheck, Row } from 'react-bootstrap';
import React, { useState } from 'react';
import { RoleDetail, RoleSelect } from '~/components/RoleSelect/RoleSelect';
import { featureFlags } from '~/featureFlags';

import { BEM } from '@feedfm/feed-util';
import { useId } from '~/hooks/useId';

export interface ApplicationAndRole {
  application: {
    id: number;
    name: string;
  };

  role: string;
}

const bem = new BEM( 'create-user-application-role' );

export interface CreateUserApplicationRolesProps {
  onAdd?: ( applicationRoles: ApplicationAndRole[] ) => void;
  className?: string;
}

const roleOptions: Map<string, { description: string; prodRole?: string; devRole?: string }> = new Map( [
  ['admin', { description: 'Admin', prodRole: 'ADMIN', devRole: 'ADMIN' }],
  ['fpadmin', { description: 'First Play Admin', prodRole: 'FIRSTPLAY_ADMIN', devRole: 'FIRSTPLAY_ADMIN' }],
  ['fpuser', { description: 'First Play User', prodRole: 'FIRSTPLAY_USER', devRole: 'FIRSTPLAY_USER' }],

  ['fprpadmin', { description: 'Replay + First Play Admin', prodRole: 'REPLAY_FIRSTPLAY_ADMIN', devRole: 'REPLAY_FIRSTPLAY_ADMIN' }],
  ['frrpuser', { description: 'Replay + First Play User', prodRole: 'REPLAY_FIRSTPLAY_USER', devRole: 'REPLAY_FIRSTPLAY_USER' }],
] );

if ( featureFlags.check( 'customer-rewrite' ) ) {
  roleOptions.set( 'replayadmin', { description: 'Replay Admin', prodRole: 'REPLAY_ADMIN', devRole: 'REPLAY_ADMIN' } );
  roleOptions.set( 'replayuser', { description: 'Replay User', prodRole: 'REPLAY_USER', devRole: 'REPLAY_USER' } );
}

/**
 * This method takes a selected role and app or app instance set, and maps it to an
 * array of applications and roles.
 *
 * The basic gist here is to deal with app vs app instance sets. For apps, we just
 * assign the role to the app. For app instance sets, we may assign the role to one
 * or both applications in the set.
 */

function mapInstanceSetAndRoleToApplicationAndRoleArray( app: SelectedApp, role: string ): ApplicationAndRole[] {
  const mappedRoles = roleOptions.get( role );

  // if the app isn't part of an instance set, we just return the app and role
  if ( !app.instanceSet ) {
    // if a mapped role was selected, use the mapped prod role, else the dev role
    if ( mappedRoles ) {
      return [
        {
          application: {
            id: app.id,
            name: app.name,
          },
          role: mappedRoles.prodRole ?? mappedRoles.devRole ?? 'unknown',
        },
      ];
    } else {
      return [
        {
          application: {
            id: app.id,
            name: app.name,
          },
          role: role,
        },
      ];
    }
  }

  if ( mappedRoles ) {
    const roles = [];

    if ( mappedRoles.prodRole ) {
      roles.push( {
        application: {
          id: app.instanceSet.productionId,
          name: app.instanceSet.productionName,
        },
        role: mappedRoles.prodRole,
      } );
    }

    if ( mappedRoles.devRole ) {
      roles.push( {
        application: {
          id: app.instanceSet.developmentId,
          name: app.instanceSet.developmentName,
        },
        role: mappedRoles.devRole,
      } );
    }

    return roles;
  }

  // by default, map to both development and production apps
  return [
    {
      application: {
        id: app.instanceSet.developmentId,
        name: app.instanceSet.developmentName,
      },
      role,
    },
    {
      application: {
        id: app.instanceSet.productionId,
        name: app.instanceSet.productionName,
      },
      role,
    },
  ];
}

export const CreateUserApplicationRoles: React.FC<CreateUserApplicationRolesProps> = ( { onAdd, className } ) => {
  const [selectedApplication, setSelectedApplication] = useState<SelectedApp | null>( null );
  const [selectedRole, setSelectedRole] = useState<string>( '' );
  const [customRole, setCustomRole] = useState<RoleDetail | null>( null );
  const id = useId();

  const handleApplicationChange = ( app: SelectedApp | null ) => {
    setSelectedApplication( app );
  };

  const handleRoleChange = ( event: React.ChangeEvent<HTMLInputElement> ) => {
    const role = event.target.value;
    setSelectedRole( role );
  };

  const handleCustomRoleChange = ( role: RoleDetail | null ) => {
    setCustomRole( role );
    setSelectedRole( 'custom' );
  };

  const handleAdd = () => {
    if ( selectedApplication && selectedRole && onAdd ) {
      let applicationAndRoles: ApplicationAndRole[];

      if ( selectedRole === 'custom' && customRole !== null ) {
        applicationAndRoles = selectedApplication.instanceSet
          ? [
            {
              application: {
                id: selectedApplication.instanceSet.developmentId,
                name: selectedApplication.instanceSet.developmentName,
              },
              role: customRole.name,
            },
            {
              application: {
                id: selectedApplication.instanceSet.productionId,
                name: selectedApplication.instanceSet.productionName,
              },
              role: customRole.name,
            },
          ]
          : [
            {
              application: {
                id: selectedApplication.id,
                name: selectedApplication.name,
              },
              role: customRole.name,
            },
          ];
      } else {
        applicationAndRoles = mapInstanceSetAndRoleToApplicationAndRoleArray( selectedApplication, selectedRole );
      }

      if ( applicationAndRoles.length > 0 && onAdd ) {
        onAdd( applicationAndRoles );

        // Reset form after adding
        setSelectedApplication( null );
        setSelectedRole( '' );
      }
    }
  };

  const isFormValid = selectedApplication && selectedRole;

  return (
    <Card className={ BEM.$( bem, className ) } bg="light">
      <Card.Body>
        <Row>
          <Form.Label column sm={ 4 }>
            Connect to:
          </Form.Label>
          <Col>
            <ApplicationSelect value={ selectedApplication } onChange={ ( app ) => handleApplicationChange( app ) } />
          </Col>
        </Row>

        <Row>
          <Form.Label column sm={ 4 }>
            Role:
          </Form.Label>
          <Form.Group as={ Col }>
            { Array.from( roleOptions.entries() ).map( ( [key, option] ) => (
              <FormCheck
                key={ key }
                type="radio"
                name="application-role"
                id={ `${id}-application-role-${key}` }
                label={ option.description }
                value={ key }
                checked={ selectedRole === key }
                onChange={ handleRoleChange }
              />
            ) ) }
            <FormCheck
              key={ 'other' }
              type="radio"
              name="application-role"
              id={ `${id}-application-role-custom` }
              value="custom"
              checked={ selectedRole === 'custom' }
              onChange={ handleRoleChange }
              className={ BEM.$( bem.el( 'custom-role' ) ) }
              inline
            />
            <RoleSelect
              value={ customRole }
              onChange={ handleCustomRoleChange }
              className={ BEM.$( bem.el( 'custom-role-select' ) ) }
            />
          </Form.Group>
        </Row>
        <Row>
          <Col>
            <Button variant="primary" onClick={ handleAdd } disabled={ !isFormValid }>
              Connect
            </Button>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

CreateUserApplicationRoles.displayName = 'CreateUserApplicationRole';
